import { View } from 'foundations-components/components/View';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HsocMerchCardGrid from 'pricing-pages-ui-hsoc/components/HsocMerchCardGrid';
import { HsocV2PageHero } from 'pricing-pages-ui-hsoc/components/HsocV2PageHero';
import HelpMeChooseInline from 'pricing-pages-ui-hsoc/components/plan-quiz/HelpMeChooseInline';
import { PlanQuizProvider } from 'pricing-pages-ui-hsoc/components/plan-quiz/PlanQuizContext';
import { PlanQuizDataProvider } from 'pricing-pages-ui-hsoc/components/plan-quiz/PlanQuizDataContext';
import { HsocV2Faqs } from 'pricing-pages-ui-components/frequentlyAskedQuestions/hsoc/HsocV2Faqs';
import { useHsocPublicPricing } from './useHsocPublicPricing';
import { PublicHsocMerchCardCTAs } from './PublicHsocMerchCardCTAs';
import { HUBSPOT_ON_CREDITS_PAGE } from '../../components/error-boundary/ErrorBoundaryNames';
import { PublicPageWrapper } from '../../components/PublicPageWrapper';
import { useCurrency } from '../../context/CurrencyContext';
import { createDeferredLoadable } from '../../utils/createDeferredLoadable';
import { HSOC_V2_SCREEN, TRACKING_SOURCES, trackClickedLink, trackFaqInteraction, trackHsocV2PageView, trackSectionViewed, trackTermSelectorInteraction } from '../../utils/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isHsocTierName = tier => tier === 'FREE' || tier === 'STARTER' || tier === 'PROFESSIONAL' || tier === 'ENTERPRISE';
const LoadablePlanQuizModal = createDeferredLoadable(() => import( /* webpackChunkName: "plan-quiz-modal" */'pricing-pages-ui-hsoc/components/plan-quiz/PlanQuizModal').then(m => ({
  default: m.default
})));
function HsocPublicPageContent() {
  const {
    displayedCurrencyCode
  } = useCurrency();
  const {
    loading,
    error,
    cards,
    productsByTier,
    showTermToggle,
    selectedTerm,
    onTermChange: setTerm
  } = useHsocPublicPricing(displayedCurrencyCode);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const handleTermChange = useCallback(term => {
    setTerm(term);
    trackTermSelectorInteraction({
      action: 'click',
      term,
      trackingSource: TRACKING_SOURCES.MERCH_CARD,
      screen: HSOC_V2_SCREEN
    });
  }, [setTerm]);
  const openQuiz = useCallback(() => setIsQuizOpen(true), []);
  const closeQuiz = useCallback(() => setIsQuizOpen(false), []);
  const planQuizData = useMemo(() => ({
    productsByTier: productsByTier !== null && productsByTier !== void 0 ? productsByTier : null,
    currencyCode: displayedCurrencyCode,
    selectedTerm,
    renderPurchaseMotions: ({
      tierName,
      productApiName,
      motions
    }) => isHsocTierName(tierName) ? /*#__PURE__*/_jsx(PublicHsocMerchCardCTAs, {
      tierName: tierName,
      productApiName: productApiName,
      motions: motions,
      currencyCode: displayedCurrencyCode,
      term: selectedTerm
    }) : null
  }), [productsByTier, displayedCurrencyCode, selectedTerm]);
  if (error) throw error;
  const showCards = !loading && cards.length > 0;
  return /*#__PURE__*/_jsx(PlanQuizProvider, {
    children: /*#__PURE__*/_jsxs(PlanQuizDataProvider, {
      value: planQuizData,
      children: [/*#__PURE__*/_jsx(HsocV2PageHero, {}), showCards && /*#__PURE__*/_jsx(View, {
        padding: "800",
        children: /*#__PURE__*/_jsx("div", {
          id: "hsoc-tier-cards",
          children: /*#__PURE__*/_jsx(HsocMerchCardGrid, {
            cards: cards,
            showTermToggle: showTermToggle,
            onTermChange: handleTermChange,
            helpMeChoose: /*#__PURE__*/_jsx(HelpMeChooseInline, {
              onOpenPlanQuiz: openQuiz
            })
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        as: "section",
        padding: "800",
        marginBlockStart: "1400",
        marginBlockEnd: "1400",
        children: /*#__PURE__*/_jsx(HsocV2Faqs, {
          onLinkClick: props => trackClickedLink(Object.assign({}, props, {
            trackingSource: TRACKING_SOURCES.FAQ,
            screen: HSOC_V2_SCREEN
          })),
          onFaqInteraction: trackFaqInteraction,
          onSectionViewed: () => trackSectionViewed({
            name: TRACKING_SOURCES.FAQ,
            action: 'scrolled in view'
          })
        })
      }), isQuizOpen && /*#__PURE__*/_jsx(LoadablePlanQuizModal, {
        onClose: closeQuiz
      })]
    })
  });
}
export default function PublicHsocPricingPage() {
  const hasTrackedPageViewRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedPageViewRef.current) {
      hasTrackedPageViewRef.current = true;
      trackHsocV2PageView();
    }
  }, []);
  return /*#__PURE__*/_jsx(PublicPageWrapper, {
    errorBoundaryName: HUBSPOT_ON_CREDITS_PAGE,
    navMarkerName: "HSOC_V2_PAGE",
    metricsRoute: "hubspot-credits",
    chunkName: "public-hsoc-pricing-page",
    children: /*#__PURE__*/_jsx(HsocPublicPageContent, {})
  });
}